import { notification } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ConfigurationItem,
  ConfigurationType,
  useAddConfigurationFieldMutation,
  useUpdateConfigurationFieldMutation,
  useUploadConfigurationImageMutation,
  useUploadConfigurationColorMutation,
} from "shared/api";
import { handleRequestError } from "shared/helpers";
import { ConfigurationForm } from "shared/ui";

/** Placeholder for `value` when POST-creating a configuration row (backend must not receive empty strings). */
const CONFIG_CREATE_PLACEHOLDER = "-";

export interface UseConfigurationsProps {
  uploadedFile: File | null;
  closeModalAndClearUploadedImage: () => void;
  values: ConfigurationForm;
  imageConfiguration?: ConfigurationItem;
  companyNameConfiguration?: ConfigurationItem;
  timezoneConfiguration?: ConfigurationItem;
  colorConfiguration?: ConfigurationItem;
  /** True if server already has a color (list row or GET /color/) — use PUT vs POST */
  hasServerColorRecord?: boolean;
  /** Current persisted color value (e.g. from GET /color/) for diffing saves */
  color?: string;
  faviconLightFile?: File | null;
  faviconDarkFile?: File | null;
  faviconLightConfiguration?: ConfigurationItem;
  faviconDarkConfiguration?: ConfigurationItem;
  onAfterSave?: () => void;
}

export const useConfigurations = ({
  uploadedFile,
  closeModalAndClearUploadedImage,
  values,
  imageConfiguration,
  companyNameConfiguration,
  timezoneConfiguration,
  colorConfiguration,
  hasServerColorRecord,
  color,
  faviconLightFile,
  faviconDarkFile,
  faviconLightConfiguration,
  faviconDarkConfiguration,
  onAfterSave,
}: UseConfigurationsProps) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();

  const [uploadConfigurationImage] = useUploadConfigurationImageMutation();
  const [addConfigurationField] = useAddConfigurationFieldMutation();
  const [updateConfigurationField] = useUpdateConfigurationFieldMutation();
  const [uploadConfigurationColor] = useUploadConfigurationColorMutation();

  const ensureConfigurationRow = async (
    key: string,
    existing: ConfigurationItem | undefined,
    valueForCreate = CONFIG_CREATE_PLACEHOLDER
  ) => {
    try {
      if (!existing) {
        await addConfigurationField({
          key,
          value: valueForCreate,
          file: null,
        }).unwrap();
      }
    } catch (error) {
      api.error({
        message: t("sidebar.error"),
      });
    }
  };

  const uploadImageForKey = async (file: File, key: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("key", key);
    await uploadConfigurationImage({ image: formData }).unwrap();
  };

  const handleCustomerImageLogo = async () => {
    try {
      if (uploadedFile) {
        await ensureConfigurationRow(
          ConfigurationType.Image,
          imageConfiguration
        );
        await uploadImageForKey(uploadedFile, ConfigurationType.Image);
      }

      api.success({
        message: t("sidebar.logoSuccessUpload"),
      });
    } catch (error) {
      handleRequestError(error, t("sidebar.error"));

      api.error({
        message: t("sidebar.error"),
      });
    } finally {
      closeModalAndClearUploadedImage();
    }
  };

  const handleAddCompanyName = async () => {
    try {
      await addConfigurationField({
        key: ConfigurationType.CompanyName,
        value: values?.companyName?.trim() || CONFIG_CREATE_PLACEHOLDER,
        file: null,
      }).unwrap();

      api.success({
        message: t("users-page.success-title"),
      });
    } catch (error) {
      api.error({
        message: t("sidebar.error"),
      });
    }
  };

  const handleUpdateCompanyName = async () => {
    try {
      await updateConfigurationField({
        key: ConfigurationType.CompanyName,
        value: values?.companyName!,
        file: null,
      }).unwrap();

      api.success({
        message: t("users-page.success-title"),
      });
    } catch (error) {
      api.error({
        message: t("sidebar.error"),
      });
    }
  };

  const handleAddTimezone = async () => {
    try {
      await addConfigurationField({
        key: ConfigurationType.Timezone,
        value: values?.timezone?.trim() || CONFIG_CREATE_PLACEHOLDER,
        file: null,
      }).unwrap();

      api.success({
        message: t("users-page.success-title"),
      });
    } catch (error) {
      api.error({
        message: t("sidebar.error"),
      });
    }
  };

  const handleUpdateTimezone = async () => {
    try {
      await updateConfigurationField({
        key: ConfigurationType.Timezone,
        value: values?.timezone!,
        file: null,
      }).unwrap();

      api.success({
        message: t("users-page.success-title"),
      });
    } catch (error) {
      api.error({
        message: t("sidebar.error"),
      });
    }
  };

  const handleColorChange = async (nextColor: string) => {
    try {
      const usePut =
        Boolean(colorConfiguration) || Boolean(hasServerColorRecord);

      if (usePut) {
        await uploadConfigurationColor({
          key: "color",
          value: nextColor,
          file: null,
        }).unwrap();
      } else {
        await addConfigurationField({
          key: "color",
          value: nextColor.trim() || CONFIG_CREATE_PLACEHOLDER,
          file: null,
        }).unwrap();
      }

      localStorage.setItem("color-theme", JSON.stringify(nextColor));

      api.success({
        message: t("sidebar.colorSuccessUpload", { color: nextColor }),
      });
    } catch (error) {
      handleRequestError(error, t("sidebar.error"));
      api.error({ message: t("sidebar.error") });
    }
  };

  const handleFaviconUpload = async (
    file: File | null | undefined,
    key: string,
    existingConfiguration?: ConfigurationItem
  ) => {
    if (!file) return;
    try {
      await ensureConfigurationRow(key, existingConfiguration);
      await uploadImageForKey(file, key);
    } catch (error) {
      api.error({ message: t("sidebar.error") });
    }
  };

  const handleConfigurationsRequests = async () => {
    await handleCustomerImageLogo();

    if (timezoneConfiguration) {
      await handleUpdateTimezone();
    } else {
      await handleAddTimezone();
    }

    if (companyNameConfiguration) {
      await handleUpdateCompanyName();
    } else {
      await handleAddCompanyName();
    }

    if (values?.color && values.color !== color) {
      await handleColorChange(values.color);
    }

    await handleFaviconUpload(
      faviconLightFile,
      "favicon-light",
      faviconLightConfiguration
    );
    await handleFaviconUpload(
      faviconDarkFile,
      "favicon-dark",
      faviconDarkConfiguration
    );

    onAfterSave?.();
    window.location.reload();
  };

  useEffect(() => {
    if (!localStorage.getItem("color-theme")) {
      handleColorChange("#002F5F");
    }
  }, []);

  return { handleConfigurationsRequests, contextHolder, handleColorChange };
};
